import { useTranslation } from 'react-i18next';
import { BuildingId, DeviceId, EcsDevice, EcsExternalId, ExternalId, Group, GroupId } from 'shared/models';
import { Loading } from 'ui/shared/loading';
import { ElevatorTile } from '../elevatorTile/ElevatorTile';
import { Permission } from 'shared/permissions';
import { useSession } from 'hooks';
import { uniq } from 'lodash';
import { sortNamesBy } from "logic/sortNames";

require('./ElevatorList.css');

export type Props = {
    buildingId: BuildingId | null;
    busy: boolean;
    editing: boolean;
    dirty: boolean;
    elevators: Group[];
    devices: Record<DeviceId, EcsDevice>;
    online: Record<GroupId, boolean>;
    externalIds: Record<GroupId, EcsExternalId[]>;
    onEdit(): void;
    onRemoveGroup(groupId: GroupId): void;
    onRemove(deviceId: DeviceId): void;
    onCancel(): void;
    onSave(): void;
};

export const ElevatorList = ({ buildingId, busy, editing, dirty, elevators, devices, online, externalIds, onRemove, onEdit, onRemoveGroup, onCancel, onSave }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();
    return (
        <div className="elevator-list">
            <table className="ui single line compact table">
                <colgroup>
                    <col style={ { width: '30%' } }/>
                    <col style={ { width: '70%' } }/>
                </colgroup>
                <thead>
                    <tr>
                        <th>
                            { t('BUILDING.ELEVATORS.COLUMN.NAME') }
                        </th>
                        <th>
                            { t('BUILDING.ELEVATORS.COLUMN.DEVICES') }
                            <span style={ { float: 'right' } }>
                                { dirty &&
                                    <button
                                        className="ui tiny red primary icon button"
                                        onClick={ onSave }
                                        disabled={ busy }
                                    >
                                        { t('COMMAND.SAVE') }
                                    </button>
                                }
                                { editing &&
                                    <button
                                        className="ui tiny icon basic button"
                                        onClick={ onCancel }
                                    >
                                        { t('COMMAND.CANCEL') }
                                    </button>
                                }
                                { !editing && hasPermission(buildingId, Permission.EditDevice) &&
                                    <button
                                        className="ui tiny icon basic button"
                                        onClick={ onEdit }
                                        disabled={ busy || Object.keys(devices).length === 0 }
                                    >
                                        <i className="ui grey setting icon"/>
                                    </button>
                                }
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    { busy && elevators.length === 0 &&
                        <tr><td colSpan={ 2 }><Loading/></td></tr>
                    }
                    { !busy && elevators.length === 0 &&
                        <tr><td colSpan={ 2 } className="__empty">{ t('BUILDING.ELEVATORS.NO_ELEVATORS') }</td></tr>
                    }
                    { elevators.sort(sortNamesBy(g => g.displayName)).map(e =>
                        <ElevatorTile
                            key={ e.id }
                            buildingId={ buildingId }
                            busy={ busy }
                            elevator={ e }
                            externalId={ uniq(externalIds[e.id]?.map(i => i.externalId))?.join(', ') }
                            devices={ devices }
                            online={ online }
                            editing={ editing }
                            onRemoveGroup={ () => onRemoveGroup(e.id) }
                            onRemove={ onRemove }
                        />
                    ) }
                </tbody>
            </table>
        </div>
    );
};
